/*
  site-header-mega.js
  WHAT: Click/focus open-close behaviour for header mega menus.
  WHY:  Mocks 02-06 reveal a full-width panel under the trigger when the user clicks
        OR keyboard-focuses a category nav item.
  WHEN: Added 2026-05-28 with mega-menu build. 2026-06-05: switched HOVER → CLICK
        per owner instruction. Mobile-parity behaviour on desktop — first tap/click
        opens the panel, then the user selects a filter chip to drill in.
        2026-06-09: second click on the SAME trigger now NAVIGATES to the category
        (used to toggle closed — read as "the menu didn't take" per owner). Close
        paths: Escape, focusout, click-outside, or clicking a DIFFERENT trigger
        (snap-swap).
  A11Y: trigger has aria-haspopup + aria-expanded; panel has aria-hidden; both flip
        on open. Escape returns focus to the trigger. Keyboard focus still opens
        the panel so tab-navigation works.
        Chip selection is marked with aria-current="true", NOT aria-pressed:
        the chips are real <a href> links and aria-pressed is only valid on a
        button role (axe-core CRITICAL). An unselected chip carries no
        aria-current attribute at all — absence is how aria-current says "no".
  Scope: ≤900px (mobile drawer territory) — early-return so mobile uses the drawer.
*/

(function () {
  'use strict';

  var MOBILE_BREAKPOINT = 1009; // kept in lockstep with the CSS media queries (rule 23)
  var openHandle = null;

  function isMobile() {
    return window.matchMedia('(max-width: ' + MOBILE_BREAKPOINT + 'px)').matches;
  }

  function getPanel(host) {
    var key = host.getAttribute('data-mega-host');
    return document.getElementById('ba-mega-panel-' + key);
  }

  function getTrigger(host) {
    return host.querySelector('[data-mega-trigger]');
  }

  function closeAll(except) {
    document.querySelectorAll('.ba-site-header__nav-item.has-mega').forEach(function (h) {
      if (h === except) return;
      var p = getPanel(h);
      var t = getTrigger(h);
      if (p) {
        p.classList.remove('is-open');
        p.setAttribute('aria-hidden', 'true');
      }
      if (t) t.setAttribute('aria-expanded', 'false');
    });
  }

  function open(host) {
    if (isMobile()) return;
    var panel = getPanel(host);
    var trigger = getTrigger(host);
    if (!panel || !trigger) return;

    // Owner 2026-06-05: when switching from an already-open panel to a
    // different one, the opacity fade-transition (~200ms each direction)
    // caused both panels to be semi-transparent simultaneously, showing
    // the page-behind through the gap. Fix: when there's an existing
    // open panel and we're switching to a new one, SNAP both — kill the
    // CSS transitions for one frame so the swap is instant. Fresh opens
    // from cold still get the smooth fade-in.
    var switching = openHandle && openHandle !== host;
    var prevPanel = switching ? getPanel(openHandle) : null;
    if (switching) {
      if (prevPanel) prevPanel.setAttribute('data-no-anim', '');
      panel.setAttribute('data-no-anim', '');
    }

    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');
    openHandle = host;
    closeAll(host);

    if (switching) {
      // Restore transitions next frame so a subsequent close gets the fade.
      requestAnimationFrame(function () {
        if (prevPanel) prevPanel.removeAttribute('data-no-anim');
        panel.removeAttribute('data-no-anim');
      });
    }
  }

  function closeHost(host) {
    var panel = getPanel(host);
    var trigger = getTrigger(host);
    if (panel) {
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
    }
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (openHandle === host) openHandle = null;
  }

  function isOpen(host) {
    var panel = getPanel(host);
    return !!(panel && panel.classList.contains('is-open'));
  }

  function init() {
    var hosts = document.querySelectorAll('.ba-site-header__nav-item.has-mega');
    if (!hosts.length) return;

    hosts.forEach(function (host) {
      // Owner 2026-06-05: click-to-open. Owner 2026-06-09 (late):
      // REVERTED the 2nd-click-navigates behaviour. The trigger is
      // now purely a panel-toggle — 1st click opens, 2nd click on
      // the same trigger closes. Navigation to the category page
      // happens via the "Help me choose →" chip (added in the same
      // session) or any of the other chips in the strip. Keeping
      // the trigger's job singular removes ambiguity.
      //
      // Close paths preserved: Escape, focusout, click-outside,
      // click-different-trigger (snap-swap inside open()).
      //
      // Mobile (≤900px) early-returns — mobile drawer pattern.
      var trigger = getTrigger(host);
      if (trigger) {
        trigger.addEventListener('click', function (e) {
          if (isMobile()) return; // mobile drawer handles this

          // SECOND CLICK NAVIGATES. Owner 2026-08-24: "the mega menu, when you
          // double click earbuds and headphones etc it should go to the collection
          // for that. at present a double click opens and then closes the mega menu."
          //
          // This REVERSES the 2026-06-09 decision recorded above, which made the
          // trigger a pure toggle so its job stayed singular. Noted so nobody flips
          // it back on the strength of that older comment: the owner has since asked
          // for the opposite, because a dead second click on a nav item that clearly
          // points at a collection reads as broken.
          //
          // The panel opens on click, not hover, so the sequence is: first click
          // opens, second click follows the href to the collection - which also makes
          // a genuine double-click do the intuitive thing.
          if (isOpen(host)) {
            return; // no preventDefault - let the browser follow the trigger's href
          }

          e.preventDefault();
          e.stopPropagation();
          open(host);
        });

        // NOTE 2026-06-05 bugfix: do NOT re-add a `focus` → open handler.
        // Mouse clicks fire focus BEFORE click. If focus opens the panel,
        // by the time click fires `isOpen` is already true → toggle closes
        // it immediately ("appears and disappears" bug). Keyboard users
        // still get the panel: pressing Enter on a focused <a> fires a
        // synthetic click event which our click handler catches.
      }

      // When focus moves entirely outside this <li> + its panel, close.
      host.addEventListener('focusout', function (e) {
        if (!host.contains(e.relatedTarget)) {
          closeHost(host);
        }
      });
    });

    // Escape closes any open panel and returns focus to the trigger that opened it.
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (!openHandle) return;
      var trigger = getTrigger(openHandle);
      var host = openHandle;
      closeAll(null);
      openHandle = null;
      if (trigger) trigger.focus();
    });

    // Click outside the header closes everything.
    document.addEventListener('click', function (e) {
      if (e.target.closest('.ba-site-header')) return;
      closeAll(null);
      openHandle = null;
    });

    // If the viewport crosses the mobile breakpoint while a panel is open, close it.
    window.addEventListener('resize', function () {
      if (isMobile() && openHandle) {
        closeAll(null);
        openHandle = null;
      }
    });

    // ─── In-panel chip filter ─────────────────────────────────────────────
    // Clicking a chip filters the tiles in THAT panel. Nothing navigates and nothing
    // reloads. Owner 2026-09-08: "it should simply filter what is shown by what matches
    // the criteria selected."
    //
    // Facet semantics: OR within a group, AND across groups. Earbuds OR Headphones,
    // AND under $99. Price is single-select within its own group because the bands are
    // nested - "Under $99" and "Under $199" both selected is meaningless, so picking one
    // replaces the other.
    //
    // Clicking an active chip deselects it. "All" (data-filter="") clears everything.
    //
    // The hrefs are deliberately left intact: chips still degrade to real tag-filtered
    // collection URLs without JS, on middle-click, and for crawlers. Design rule 6 makes
    // those URLs the locked AEO surface, so they are not ours to remove.

    // Tag-match set. Products tag activities as `ia_nav_<topic>`, categories as
    // `ia_cat_<topic>`, and hardware features as `f_<topic>`; chip handles use hyphens
    // where tags use underscores. Every one of those prefixes was added because a chip
    // came back blank without it - `f_` for the Speakers "Portable" chip (2026-06-18),
    // `ia_cat_` for the Deals category chips (2026-08-24). Widen, never narrow.
    function buildMatchSet(filter) {
      if (!filter) return null;
      var hyphen = filter;
      var underscore = filter.replace(/-/g, '_');
      var set = {};
      set[hyphen] = true;
      set[underscore] = true;
      set['ia_nav_' + hyphen] = true;
      set['ia_nav_' + underscore] = true;
      set['f_' + hyphen] = true;
      set['f_' + underscore] = true;
      set['ia_cat_' + hyphen] = true;
      set['ia_cat_' + underscore] = true;
      return set;
    }

    function tileHasAnyTag(tile, matchSets) {
      if (!matchSets.length) return true;               // no tag facet selected
      var tags = (tile.getAttribute('data-tags') || '').toLowerCase().split(',');
      for (var s = 0; s < matchSets.length; s++) {
        for (var i = 0; i < tags.length; i++) {
          var t = tags[i].trim();
          if (t && matchSets[s][t]) return true;        // OR within the group
        }
      }
      return false;
    }

    function tileUnderPrice(tile, priceMax) {
      if (priceMax === null) return true;               // no price facet selected
      var p = parseInt(tile.getAttribute('data-price'), 10);
      if (isNaN(p)) return true;                        // never hide on missing data
      return p <= priceMax;
    }

    // How many tiles survive a given selection. Used both to render and to decide
    // whether a chip is worth offering at all.
    function countMatching(tiles, tagFilters, priceMax) {
      var sets = tagFilters.map(buildMatchSet);
      var n = 0;
      for (var i = 0; i < tiles.length; i++) {
        if (tileHasAnyTag(tiles[i], sets) && tileUnderPrice(tiles[i], priceMax)) n++;
      }
      return n;
    }

    function chipsOf(panel) {
      return Array.prototype.filter.call(
        panel.querySelectorAll('.ba-mega__chip'),
        function (c) {
          // Link chips are not facets: the gift CTA, anything marked --nav, and
          // anything carrying neither a tag nor a price attribute.
          if (c.classList.contains('ba-mega__chip--gift')) return false;
          if (c.classList.contains('ba-mega__chip--nav')) return false;
          return c.hasAttribute('data-filter') || c.hasAttribute('data-filter-price-max');
        }
      );
    }

    function readSelection(panel) {
      var tags = [];
      var priceMax = null;
      chipsOf(panel).forEach(function (c) {
        if (!c.classList.contains('is-active')) return;
        if (c.hasAttribute('data-filter-price-max')) {
          priceMax = parseInt(c.getAttribute('data-filter-price-max'), 10);
        } else {
          var f = (c.getAttribute('data-filter') || '').trim().toLowerCase();
          if (f) tags.push(f);
        }
      });
      return { tags: tags, priceMax: priceMax };
    }

    function applyPanel(panel) {
      var tiles = Array.prototype.slice.call(panel.querySelectorAll('.ba-ptile-wrap'));
      var sel = readSelection(panel);
      var sets = sel.tags.map(buildMatchSet);

      tiles.forEach(function (tile) {
        var show = tileHasAnyTag(tile, sets) && tileUnderPrice(tile, sel.priceMax);
        // The grid child is .ba-ptile-wrap, not the inner <a>. Hiding the anchor left
        // the wrap holding its grid cell, so visible tiles could not reflow to the top
        // left - fixed 2026-06-18, and the same trap exists in ba-chip-active.js.
        if (show) tile.classList.remove('is-hidden');
        else tile.classList.add('is-hidden');
      });

      var grid = panel.querySelector('.ba-mega__grid');
      if (grid) {
        grid.setAttribute('data-active-filter', sel.tags.join(' '));
        grid.setAttribute('data-visible-count', String(countMatching(tiles, sel.tags, sel.priceMax)));
      }

      // Preclusion. Owner: "when one is selected that precludes another it needs to be
      // removed." For each chip, ask what the count WOULD be if it were added to the
      // current selection; if zero, take it off the strip. A chip that is already
      // selected is never removed - you have to be able to click it off again.
      chipsOf(panel).forEach(function (c) {
        var isAll = c.hasAttribute('data-filter') &&
                    (c.getAttribute('data-filter') || '').trim() === '';
        if (c.classList.contains('is-active') || isAll) {
          c.classList.remove('is-unavailable');
          c.removeAttribute('aria-disabled');
          return;
        }
        // Evaluate the chip against the OTHER groups, not as an addition to its own.
        // Within a group the logic is OR, so adding a tag widens the set and could never
        // empty it - asking "would this empty the panel?" precludes nothing. Asking
        // "does this match anything given the other constraints?" is the real question,
        // and is how Shopify's own facet counts behave.
        var tags, priceMax;
        if (c.hasAttribute('data-filter-price-max')) {
          tags = sel.tags;                                    // tag group unchanged
          priceMax = parseInt(c.getAttribute('data-filter-price-max'), 10);
        } else {
          var f = (c.getAttribute('data-filter') || '').trim().toLowerCase();
          tags = f ? [f] : [];                                // THIS tag alone
          priceMax = sel.priceMax;                            // price group unchanged
        }
        if (countMatching(tiles, tags, priceMax) === 0) {
          c.classList.add('is-unavailable');
          c.setAttribute('aria-disabled', 'true');
        } else {
          c.classList.remove('is-unavailable');
          c.removeAttribute('aria-disabled');
        }
      });
    }

    document.addEventListener('click', function (e) {
      var chip = e.target.closest('.ba-mega__chip');
      if (!chip) return;

      // Real links, not facets: the gift CTA, chips marked --nav (a query-string URL with
      // no filterable data), and the "help me choose" guide chip, which has no filter
      // attribute at all. Owner 2026-08-25: "the help me choose buttons are going
      // nowhere" - it used to fall through and be treated as an empty filter, which reset
      // the panel and swallowed the click. Anything without a filter attribute is a link.
      if (chip.classList.contains('ba-mega__chip--gift')) return;
      if (chip.classList.contains('ba-mega__chip--nav')) return;
      if (!chip.hasAttribute('data-filter') &&
          !chip.hasAttribute('data-filter-price-max')) return;

      e.preventDefault();
      var panel = chip.closest('.ba-mega');
      if (!panel) return;

      // A removed chip is not clickable.
      if (chip.classList.contains('is-unavailable')) return;

      var isPrice = chip.hasAttribute('data-filter-price-max');
      var isAll = !isPrice && (chip.getAttribute('data-filter') || '').trim() === '';
      var wasActive = chip.classList.contains('is-active');

      if (isAll) {
        chipsOf(panel).forEach(function (c) {
          c.classList.remove('is-active');
          c.removeAttribute('aria-current');
        });
      } else if (wasActive) {
        // Toggle off.
        chip.classList.remove('is-active');
        chip.removeAttribute('aria-current');
      } else {
        var strip = chip.closest('.ba-mega__chips');
        var single = strip && strip.getAttribute('data-chip-select') === 'single';

        if (isPrice) {
          // Single-select within the price group - the bands are nested.
          chipsOf(panel).forEach(function (c) {
            if (c.hasAttribute('data-filter-price-max')) {
              c.classList.remove('is-active');
              c.removeAttribute('aria-current');
            }
          });
        } else if (single) {
          // Owner 2026-09-08: on a single-select strip a chip must not stay lit when
          // another is picked - the new pick REPLACES it. Clears sibling TAG chips
          // only; a price chip in the same strip is an independent group and keeps
          // its own single-select rule above.
          chipsOf(panel).forEach(function (c) {
            if (c.hasAttribute('data-filter') &&
                (c.getAttribute('data-filter') || '').trim() !== '') {
              c.classList.remove('is-active');
              c.removeAttribute('aria-current');
            }
          });
        }
        chip.classList.add('is-active');
        chip.setAttribute('aria-current', 'true');
      }

      // Keep "All" lit only when nothing else is.
      var sel = readSelection(panel);
      var nothingSelected = sel.tags.length === 0 && sel.priceMax === null;
      chipsOf(panel).forEach(function (c) {
        var thisIsAll = c.hasAttribute('data-filter') &&
                        (c.getAttribute('data-filter') || '').trim() === '';
        if (!thisIsAll) return;
        if (nothingSelected) {
          c.classList.add('is-active');
          c.setAttribute('aria-current', 'true');
        } else {
          c.classList.remove('is-active');
          c.removeAttribute('aria-current');
        }
      });

      applyPanel(panel);
    });

    // Compute preclusion once up front, so a chip that can never match anything is not
    // offered before the first click.
    //
    // BA-CUSTOM [ref: mega-skip-on-mobile] NOT on mobile. `.ba-mega` is `display: none` at
    // <=1009px (site-header-mega.css:242) and the mobile drawer is a separate component, so
    // below that width this sweep computes chip preclusion for UI that cannot be seen or
    // opened. Measured cost: 5 panels x 37 tiles x 40 chips, roughly 1,500 tile evaluations
    // synchronously at DOMContentLoaded - on a phone, for nothing. Owner 2026-10-02: "if
    // mobile is not displaying mega menu loadiong it is a waste of time and data".
    //
    // The work is not skipped, only deferred: if the viewport ever crosses up to desktop
    // (rotation, a tablet, a resized window) the sweep runs then, so a panel is never opened
    // without its preclusion state computed.
    if (!isMobile()) {
      document.querySelectorAll('.ba-mega').forEach(applyPanel);
    } else {
      var desktopMq = window.matchMedia('(min-width: ' + (MOBILE_BREAKPOINT + 1) + 'px)');
      var sweepOnce = function () {
        if (!desktopMq.matches) return;
        document.querySelectorAll('.ba-mega').forEach(applyPanel);
        desktopMq.removeEventListener('change', sweepOnce);
      };
      desktopMq.addEventListener('change', sweepOnce);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
